import { BellRing, Loader2, MonitorSmartphone, Send, Trash2 } from 'lucide-react';
import {
  useDisablePush,
  useEnablePush,
  useLocalPushDevice,
  usePushDevices,
  usePushSupport,
  useTestDestination,
  useVapidKey,
} from '@/hooks/usePush.ts';
// `useTestDestination` above is a re-export from `useNotifications` — see the note
// there. Imported through this module so the push UI keeps one import site.
import axios from 'axios';
import { Button } from '@/components/ui/button.tsx';
import { Badge } from '@/components/ui/badge.tsx';

/** A 429 is the cooldown, not a broken destination — worth saying differently. */
function isRateLimited(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 429;
}

/**
 * Enrolling a browser for push (§5.3).
 *
 * **The prompt is the scarce resource.** Browsers auto-quiet origins that ask early and
 * get denied, and a denial is close to irreversible from inside the page — the reset is
 * several clicks into site settings. So there is exactly one path to a prompt here: the
 * Enable button, on a click. A denied browser gets an explanation and no retry button,
 * because a retry cannot work and offering it teaches people to distrust the control.
 *
 * Lives beside the rules list rather than inside the older preferences matrix, which
 * Phase 5 retires — push is a delivery target for *rules*, so it belongs with them.
 */
/** Enrolment date, short and local — the tiebreaker between two identical labels. */
function formatEnrolled(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function PushEnrollment() {
  const { supported, permission, needsInstall } = usePushSupport();
  const { data: vapidKey, isLoading: keyLoading } = useVapidKey();
  const { data: devices } = usePushDevices();
  const { data: localDeviceId } = useLocalPushDevice(supported && permission === 'granted');
  const enable = useEnablePush();
  const disable = useDisablePush();
  const test = useTestDestination();

  const heading = (
    <div className="flex items-center gap-2">
      <BellRing className="h-4 w-4 text-muted-foreground" />
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Push notifications</h3>
    </div>
  );

  // Wrapped so every branch below renders the same frame.
  const frame = (children: React.ReactNode) => (
    <div className="space-y-2">
      {heading}
      {children}
    </div>
  );

  // iOS exposes the Push API only to Home Screen web apps, so an iPhone in a Safari tab
  // lands here with an action available rather than a dead end. Saying "unsupported" would
  // be false, and it is the state every iPhone user starts in.
  if (needsInstall) {
    return frame(
      <p className="text-xs text-muted-foreground">
        To get notifications on this device, add the app to your Home Screen: tap the Share button, then
        &ldquo;Add to Home Screen&rdquo;. Then turn notifications on from here.
      </p>,
    );
  }

  if (!supported) {
    return frame(
      <p className="text-xs text-muted-foreground">
        This browser can't receive push notifications. It needs a secure (HTTPS) connection and support for
        service workers.
      </p>,
    );
  }

  if (keyLoading) {
    return frame(
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking availability…
      </div>,
    );
  }

  // 503 from the key endpoint: a valid deployment state, not a fault.
  if (!vapidKey) {
    return frame(
      <p className="text-xs text-muted-foreground">Push notifications aren't configured on this deployment.</p>,
    );
  }

  if (permission === 'denied') {
    return frame(
      <p className="text-xs text-muted-foreground">
        You've blocked notifications for this site, so this browser can't be enrolled. To change it, open your
        browser's site settings for this page and allow notifications — we can't re-ask from here.
      </p>,
    );
  }

  const enrolled = devices ?? [];
  const thisBrowserEnrolled = localDeviceId != null && enrolled.some((d) => d.id === localDeviceId);

  return frame(
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Get notified on a device even when this tab is closed. Enrol each browser you want notified, then send
        push from a notification rule.
      </p>

      {enrolled.map((device) => {
        const isThisBrowser = device.id === localDeviceId;
        return (
          <div
            key={device.id}
            className="flex items-center justify-between gap-3 rounded-md border border-border-subtle px-3 py-2"
          >
            <span className="flex items-start gap-2 text-sm min-w-0">
              <MonitorSmartphone className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <span className="min-w-0">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="truncate">{device.displayName}</span>
                  {isThisBrowser ? (
                    <Badge className="bg-sev-4/15 text-sev-4 border-0 font-normal">This browser</Badge>
                  ) : null}
                </span>
                {/* A page cannot read a machine's hostname, so two identical browsers on
                    two identical systems produce the same label. The enrolment date is
                    what tells those apart. */}
                <span className="block text-xs text-muted-foreground">Added {formatEnrolled(device.createdAt)}</span>
              </span>
            </span>
            <span className="flex items-center gap-1.5 shrink-0">
              {/* Only offered for THIS browser: a test sent to another device lands
                  somewhere the user cannot see, so a silent failure there would read as
                  a success here. */}
              {isThisBrowser ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { test.mutate(device.id); }}
                  disabled={test.isPending}
                >
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                  {test.isPending ? 'Sending…' : 'Send test'}
                </Button>
              ) : null}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => { disable.mutate({ id: device.id, thisBrowser: isThisBrowser }); }}
                disabled={disable.isPending}
                aria-label={`Remove ${device.displayName}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </span>
          </div>
        );
      })}

      {test.isSuccess && !test.data.delivered ? (
        <p className="text-xs text-destructive">
          {test.data.detail ?? "The test notification couldn't be delivered."}
        </p>
      ) : null}
      {test.isSuccess && test.data.delivered ? (
        <p className="text-xs text-muted-foreground">
          Test sent. If nothing appears, check this browser's notification permissions for the site.
        </p>
      ) : null}
      {test.isError ? (
        <p className="text-xs text-destructive">
          {isRateLimited(test.error)
            ? 'Just tested — give it a minute before trying again.'
            : "Couldn't send a test notification."}
        </p>
      ) : null}

      {thisBrowserEnrolled ? null : (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => { enable.mutate(vapidKey); }} disabled={enable.isPending}>
            {enable.isPending ? 'Enabling…' : 'Enable push on this browser'}
          </Button>
          {enable.isError ? (
            <span className="text-xs text-destructive">
              {enable.error instanceof Error && enable.error.message === 'permission-denied'
                ? "You declined the permission prompt, so this browser wasn't enrolled."
                : "Couldn't enable push on this browser."}
            </span>
          ) : null}
        </div>
      )}
    </div>,
  );
}
